import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

// --- Type Definitions for DataWarehouseJob ---

interface DataWarehouseParticipantInfo {
    id: string;
    name: string;
    googleProfilePicture?: string;
}

interface DataWarehouseApplicationInfo {
    id: string;
    applicationName: string;
}

interface DataWarehouseSpecificDetails {
    projectId?: string;
    datasetId?: string;
    region?: string;
    pricingModel?: string;
}

interface DataWarehouseJob {
    id: string;
    name: string;
    application?: DataWarehouseApplicationInfo;
    cloudProvider?: string;
    solutionCategoryType?: string; // e.g., 'bigquery', 'redshift'
    costLabelPairs?: { cost: string; label: string }[];
    status: 'pending' | 'active' | 'rejected' | 'approved' | 'provisioning' | 'failed' | string;
    details?: {
        dataWarehouse?: DataWarehouseSpecificDetails;
    };
    reasonForRejection?: string;
    createdAt?: string;
    updatedAt?: string;
    solutionOwner?: DataWarehouseParticipantInfo | null;
    solutionApprover?: DataWarehouseParticipantInfo | null;
}

interface DataWarehousesTableProps {
    jobs: DataWarehouseJob[];
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected' | string;
}

const DataWarehousesTable: React.FC<DataWarehousesTableProps> = ({
    jobs,
    columnFilters,
    statusType,
}) => {
    const navigate = useNavigate();

    const handleRowClick = (jobId: string) => {
        navigate(`/datasolutions/data-warehouses/details/${jobId}`);
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
        if (!dateValue) return "N/A";
        const numericDate = Number(dateValue);
        if (isNaN(numericDate) || numericDate === 0) {
            const isoDate = moment(dateValue, moment.ISO_8601, true);
            return isoDate.isValid() ? isoDate.format("DD/MM/YYYY | hh:mm A") : "N/A";
        }
        return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
    };
    
    const headers = [
        { key: "name", label: "WAREHOUSE NAME", xs: 2.5 },
        { key: "application", label: "APPLICATION", xs: 1.5 },
        { key: "cloudProvider", label: "CLOUD PROVIDER", xs: 1.5 },
        { key: "dataWarehouseType", label: "TYPE", xs: 1.5 },
        { key: "region", label: "REGION", xs: 1.5 },
        { key: "solutionOwner", label: "OWNER", xs: 1.5 },
        { key: "createdAt", label: "ADDED ON", xs: 1.5 },
    ];

    let iconColor = "#FDB022";
    let listItemBgColor = "#FDB02210";

    if (statusType === "active" || statusType === "approved" || statusType === "success") {
        iconColor = "#12B76A";
        listItemBgColor = "#FFFFFF";
    } else if (statusType === "rejected" || statusType === "failed") {
        iconColor = "#F04438";
        listItemBgColor = "#F0443810";
    }

    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {jobs.map((job) => (
                <ListItem
                    key={job.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        backgroundColor: listItemBgColor,
                        
                    }}
                    onClick={() => handleRowClick(job.id)}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.name && (
                                <Grid item xs={headers.find(h => h.key === 'name')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon sx={{ color: iconColor, bgcolor: iconColor, width: "4px", height: "22px", marginRight: "8px" }} />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {job.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.application && (
                                <Grid item xs={headers.find(h => h.key === 'application')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {job.application?.applicationName || "-"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {job.cloudProvider?.toUpperCase() || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                             {columnFilters.dataWarehouseType && (
                                <Grid item xs={headers.find(h => h.key === 'dataWarehouseType')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {job.solutionCategoryType || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.region && (
                                <Grid item xs={headers.find(h => h.key === 'region')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {job.details?.dataWarehouse?.region || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.solutionOwner && (
                                <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                            alt={job.solutionOwner?.name || ""}
                                            src={job.solutionOwner?.googleProfilePicture} // <-- ADD THIS LINE
                                            sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: 'rgba(0,0,0,0.1)' }}
                                        >
                                            {/* This initial is now a fallback */}
                                            {job.solutionOwner && job.solutionOwner.name ? job.solutionOwner.name.charAt(0).toUpperCase() : null}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {job.solutionOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(job.createdAt)}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default DataWarehousesTable;